import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import {
    Button,
    GlobeIcon,
    MailIcon,
    PhoneIcon,
    SelectField,
    TextField,
    Typography,
    UserIcon,
} from '@eventbrite/marmalade';
import React from 'react';
import {
    Controller,
    FormProvider,
    useForm,
    useFormContext,
} from 'react-hook-form';
import { ThumbsUp } from '../../assets/ThumbsUp';
import {
    COUNTRY_PREFIX_LIST,
    COUNTRY_TO_PREFIX_MAPPING,
    MODAL_FEATURES,
    MODAL_FEATURE_LEAD,
    MODAL_FORM_COMPLETE_DESCRIPTION,
    MODAL_FORM_COMPLETE_TITLE,
} from '../../constants';
import {
    CohortProps,
    EntryPointProps,
    ModalFormValues,
    SubmitFormProps,
} from '../../types';
import { saveSalesEligibleAnalytics } from '../../utils';

const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
};

const PHONE_NUMBER_MIN_LENGTH = 6;

export const FirstNameField = () => {
    const { control } = useFormContext<ModalFormValues>();
    const requiredRule = {
        value: true,
        message: gettext('Please enter your first name.').toString(),
    };

    return (
        <Controller
            name="firstName"
            control={control}
            rules={{ required: requiredRule }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                    id="first-name"
                    label={gettext('First Name')}
                    type="text"
                    prefix={<UserIcon />}
                    required
                    hasError={!!error}
                    helperText={error?.message}
                    value={value || ''}
                    onChange={onChange}
                />
            )}
        />
    );
};

export const LastNameField = () => {
    const { control } = useFormContext<ModalFormValues>();
    const requiredRule = {
        value: true,
        message: gettext('Please enter your last name.').toString(),
    };

    return (
        <Controller
            name="lastName"
            control={control}
            rules={{ required: requiredRule }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                    id="last-name"
                    label={gettext('Last Name')}
                    type="text"
                    prefix={<UserIcon />}
                    required
                    hasError={!!error}
                    helperText={error?.message}
                    value={value || ''}
                    onChange={onChange}
                />
            )}
        />
    );
};

export const EmailField = () => {
    const { control } = useFormContext<ModalFormValues>();

    const emailMessage = gettext(
        'Please enter a valid email address.',
    ).toString();

    const validateEmail = (value: string) => {
        if (value && !isValidEmail(value)) {
            return emailMessage;
        }
        return true;
    };

    return (
        <Controller
            name="email"
            control={control}
            rules={{
                required: {
                    value: true,
                    message: emailMessage,
                },
                validate: {
                    validEmail: validateEmail,
                },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                    id="email"
                    label={gettext('Email')}
                    type="email"
                    prefix={<MailIcon />}
                    required
                    hasError={!!error}
                    helperText={error?.message}
                    value={value || ''}
                    onChange={onChange}
                />
            )}
        />
    );
};

export const PhoneField = () => {
    const { control } = useFormContext<ModalFormValues>();

    const phoneMessage = gettext(
        'Please enter a valid phone number.',
    ).toString();

    const validatePhone = (value: string) => {
        if (value && value.length < PHONE_NUMBER_MIN_LENGTH) {
            return phoneMessage;
        }
        return true;
    };

    return (
        <Controller
            name="phoneNumber"
            control={control}
            rules={{
                required: {
                    value: true,
                    message: phoneMessage,
                },
                validate: {
                    validEmail: validatePhone,
                },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                    id="phone-number"
                    label={gettext('Phone number')}
                    type="number"
                    prefix={<PhoneIcon />}
                    required
                    hasError={!!error}
                    helperText={error?.message}
                    value={value || ''}
                    onChange={onChange}
                />
            )}
        />
    );
};

export const CountryCodeField = () => {
    const { control } = useFormContext<ModalFormValues>();

    const requiredRule = {
        value: true,
        message: gettext('Country code is required.').toString(),
    };

    return (
        <Controller
            name="countryCode"
            control={control}
            rules={{ required: requiredRule }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SelectField
                    id="country-code"
                    options={COUNTRY_PREFIX_LIST}
                    label={gettext('Country')}
                    prefix={!value && <GlobeIcon />}
                    required
                    hasError={!!error}
                    helperText={error?.message}
                    value={value || ''}
                    onChange={onChange}
                />
            )}
        />
    );
};

export const SalesEligibleModalForm = ({
    entryPoint,
    cohort,
    submittedForm,
}: {
    entryPoint: EntryPointProps;
    cohort: CohortProps;
    submittedForm: (formData: SubmitFormProps) => void;
}) => {
    const {
        activeOrganizationId: activeOrgId = '',
        user: {
            firstName = '',
            lastName = '',
            email = '',
            phoneNumber = '',
            publicId = '',
        } = {},
        request: {
            default_location_for_tld: { country_code: countryCode = '' } = {},
        } = {},
    } = HAS_WINDOW ? window.__SERVER_DATA__ || {} : {};

    const methods = useForm<ModalFormValues>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            firstName,
            lastName,
            email,
            phoneNumber,
            countryCode,
        },
    });

    const submitForm = async (values: ModalFormValues) => {
        const { firstName, lastName, email, phoneNumber, countryCode } = values;
        const phone = `${COUNTRY_TO_PREFIX_MAPPING[countryCode]}${phoneNumber}`;

        // For the Logged Out Event Create flow, we track the analytics event
        // after the user has completed creating account.
        if (entryPoint !== EntryPointProps.LoggedOutEventCreate) {
            await saveSalesEligibleAnalytics({
                userId: publicId,
                phoneNumber: phone,
                firstName,
                lastName,
                email,
                cohort,
                activeOrgId,
            });
        }
        localStorage.setItem('salesEligibleCompleted', 'true');
        const formData = {
            firstName,
            lastName,
            email,
            phoneNumber: phone,
        };
        submittedForm(formData);
    };

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(submitForm)}
                data-testid="sales-eligible-modal-form"
            >
                <div className="sales-eligible-modal__form-row">
                    <div className="sales-eligible-modal__form-name">
                        <FirstNameField />
                    </div>
                    <div className="sales-eligible-modal__form-name">
                        <LastNameField />
                    </div>
                </div>
                <EmailField />
                <div className="sales-eligible-modal__form-row">
                    <div className="sales-eligible-modal__form-country">
                        <CountryCodeField />
                    </div>
                    <div className="sales-eligible-modal__form-phone">
                        <PhoneField />
                    </div>
                </div>
                <Button
                    className="sales-eligible-modal__submit-button"
                    variant="primary"
                    size="block"
                    type="submit"
                    data-testid="sales-eligible-modal-submit-button"
                >
                    {gettext('Sign up to be contacted')}
                </Button>
            </form>
        </FormProvider>
    );
};

export const SalesEligibleComplete = () => {
    return (
        <div
            className="sales-eligible-modal__complete"
            data-testid="sales-eligible-modal-complete"
        >
            <div className="sales-eligible-modal__complete-text">
                <Typography
                    variant="heading-sm"
                    color="neutral-800"
                    className="sales-eligible-modal__complete-title"
                >
                    {MODAL_FORM_COMPLETE_TITLE}
                </Typography>
                <Typography
                    variant="body-md"
                    color="neutral-800"
                    className="sales-eligible-modal__complete-description"
                >
                    {MODAL_FORM_COMPLETE_DESCRIPTION}
                </Typography>
            </div>
            <div className="sales-eligible-modal__complete-icon">
                <ThumbsUp />
            </div>
        </div>
    );
};

export const FeatureList = () => (
    <>
        <div className="sales-eligible-modal__feature-lead">
            {MODAL_FEATURE_LEAD}
        </div>
        {MODAL_FEATURES.map((feature, index) => (
            <div key={index} className="sales-eligible-modal__feature-item">
                <div className="sales-eligible-modal__feature-icon">
                    <feature.icon />
                </div>
                <div className="sales-eligible-modal__feature-content">
                    <Typography variant="body-md-bold" color="neutral-900">
                        {feature.title}
                    </Typography>
                    <Typography variant="body-md" color="neutral-900">
                        {feature.description}
                    </Typography>
                </div>
            </div>
        ))}
    </>
);
